import { Icon, Checkbox, Button } from "@thetreep/ui";
import Stepper from "@/components/common/Stepper";
import { BannerMessage } from "@components/common/BannerMessage";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { useLocation } from "react-router";
import { routes } from "@routes";
import { useNavigate } from "react-router";
import { useBookingStore } from "@stores/useBookingStore";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Ancillary, LocalDateTime, Offer } from "@gen/travel/v1/common_pb";
import { AncillaryToBook, AncillaryToBookSchema } from "@gen/travel/v1/cart_pb";
import { create } from "@bufbuild/protobuf";
import { formatPrice } from "@utils/formatPrice";
import { searchJourney, toInwardRequest } from "@services/travel";
import { useLoadingEffect } from "@hooks/useLoadingEffect";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";
import { SearchLoader } from "../search/SearchLoader";
import { useDAP } from "../dap.hook";
import { ExchangeOffer } from "@gen/travel/v1/exchange_pb";
import { localDateTimeToDate } from "@utils/date";

export default function Options() {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const {
    outwardOffer,
    inwardOffer,
    searchRequest,
    outwardResults,
    setOutwardOffer,
    setInwardOffer,
    setInwardResults,
  } = useBookingStore();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useLoadingEffect(isLoading);

  const { addToast } = useToaster();
  const { dap } = useDAP();
  const nbPassengers = (searchRequest?.additionalPassengers?.length ?? 0) + 1;
  const type = location.state?.type;

  // prompt for outward or inward options, depending of where we are in the booking flow.
  // if inwardOffer is undefined, we are in the outward flow.
  const offer = useMemo(
    () => (inwardOffer && type == "inward" ? inwardOffer : outwardOffer),
    [outwardOffer, inwardOffer],
  );

  const origin = offer?.solution?.journey?.route?.origin?.name;
  const destination = offer?.solution?.journey?.route?.destination?.name;
  const date = offer?.solution?.journey?.schedule?.departure;

  function formatDate(ldt?: LocalDateTime) {
    if (!ldt) return "";
    const date = localDateTimeToDate(ldt);
    const locale = i18n.language === "fr" ? fr : enUS;
    const formatString =
      i18n.language === "fr" ? "EEEE, d MMMM yyyy" : "EEEE, MMMM d, yyyy";
    return format(date, formatString, { locale });
  }

  const [selectedAncillaries, setSelectedAncillaries] = useState<
    AncillaryToBook[]
  >([]);

  function addAncillary(type: string, quantity: number) {
    const updatedAncillaries = selectedAncillaries.filter(
      (a) => a.type !== type,
    );
    if (quantity > 0) {
      const newAncillary = create(AncillaryToBookSchema, { type, quantity });
      updatedAncillaries.push(newAncillary);
    }
    setSelectedAncillaries(updatedAncillaries);
  }

  const handleConfirm = async () => {
    const searchRequest = location.state?.searchRequest;

    if (outwardOffer && !inwardOffer) {
      setIsLoading(true);
      try {
        const response = await searchJourney(
          toInwardRequest(
            searchRequest,
            dap,
            outwardOffer.offer.id,
            outwardResults?.travelId,
          ),
        );
        setInwardResults(response);
        setOutwardOffer({ ...outwardOffer, ancillaries: selectedAncillaries });
        navigate(routes.RESULTS + search, {
          state: { searchRequest, searchResponse: response },
        });
      } catch (e) {
        if (e instanceof ConnectError) {
          addToast("error", e.rawMessage);
        }
      } finally {
        setIsLoading(false);
      }
    } else if (outwardOffer && inwardOffer) {
      if (type == "outward") {
        setOutwardOffer({ ...outwardOffer, ancillaries: selectedAncillaries });
      } else {
        setInwardOffer({ ...inwardOffer, ancillaries: selectedAncillaries });
      }

      navigate(routes.RECAP + search);
    }
  };

  function ancillariesForOffer(offer: Offer | ExchangeOffer | undefined) {
    if (!offer) return;
    const uniqueAncillaries = offer.ancillaries.filter(
      (a, i, arr) => arr.findIndex((x) => x.type === a.type) === i,
    );

    return uniqueAncillaries.map((a, i) => {
      switch (a.type) {
        case "additional-luggage":
          return (
            <AdditionalLuggage
              key={i}
              ancillary={a}
              onChange={(q) => addAncillary("additional-luggage", q)}
            />
          );
        case "ouigo-flex":
          return (
            <OuigoFlex
              key={i}
              ancillary={a}
              onChange={(q) => addAncillary("ouigo-flex", q)}
            />
          );
        case "bike":
          return (
            <FoldedBike
              key={i}
              ancillary={a}
              onChange={(q) => addAncillary("bike", q)}
            />
          );
        case "disassembled-bike":
          return (
            <DisassembledBike
              key={i}
              ancillary={a}
              onChange={(q) => addAncillary("disassembled-bike", q)}
            />
          );
        default:
          console.error(`unsupported ancillary type: ${a.type}`);
      }
    });
  }

  function totalCost() {
    const cost = selectedAncillaries.reduce((acc, a) => {
      const ancillary = offer?.offer.ancillaries?.find(
        (a2) => a2.type === a.type,
      );
      if (!ancillary || !ancillary.price) return acc;

      return acc + a.quantity * Number(ancillary.price.amount) * nbPassengers;
    }, 0);
    return formatPrice({
      amount: BigInt(cost),
      currency: offer?.offer.ancillaries[0]?.price?.currency,
    });
  }

  return (
    <div>
      {isLoading && <SearchLoader type={"return"} />}
      {outwardOffer && type == "outward" && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="text-[32px] font-bold font-jakarta text-text-strong">
              {t("search.outward")}: {origin} → {destination}
            </div>
            <div className="text-[20px] text-text-strong font-jakarta">
              {formatDate(date)}
            </div>
          </div>

          <h2 className="text-[16px] md:text-[24px] font-bold font-jakarta text-text-strong">
            Options
          </h2>

          {ancillariesForOffer(outwardOffer?.offer)}

          <div className="flex gap-4 items-center justify-end">
            <p>{t("options.additionalCost", { cost: totalCost() })}</p>
            <Button onClick={handleConfirm}>{t("options.next")}</Button>
          </div>
        </div>
      )}
      {inwardOffer && type == "inward" && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="text-[32px] font-bold font-jakarta text-text-strong">
              {t("search.return")}: {destination} → {origin}
            </div>
            <div className="text-[20px] text-text-strong font-jakarta">
              {formatDate(date)}
            </div>
          </div>

          <h2 className="text-[16px] md:text-[24px] font-bold font-jakarta text-text-strong">
            Options
          </h2>

          {ancillariesForOffer(inwardOffer?.offer)}

          <div className="flex gap-4 items-center justify-end">
            <p>{t("options.additionalCost", { cost: totalCost() })}</p>
            <Button onClick={handleConfirm}>{t("options.next")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function OuigoFlex({ ancillary, onChange }: AncillaryProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(false);

  function handleChange() {
    const v = !value;
    setValue(v);
    onChange(v ? 1 : 0);
  }

  return (
    <div className="block-section flex flex-col md:flex-row md:justify-between gap-4 mb-6">
      <div className="flex gap-4">
        <Icon src="shuffle_2_line" />
        <div className="text-base text-text-strong">
          <div className="font-jakarta  uppercase text-[20px]">Ouigoflex</div>
          <p>{t("options.ouigoflexText")}</p>
          <a className="font-bold">{t("options.seeConditions")}</a>
        </div>
      </div>

      <div className="flex flex-row gap-2 justify-between md:flex-col md:text-right md:items-end">
        <div className="md:order-1">
          <p>
            {t("options.ouigiflexAdult", {
              price: formatPrice(ancillary.price),
            })}
          </p>
          <p>{t("options.ouigiflexChild")}</p>
        </div>
        <Checkbox
          checkmarkType="toggle"
          checked={value}
          onChange={handleChange}
        />
      </div>
    </div>
  );
}

interface AncillaryProps {
  ancillary: Ancillary;
  onChange: (quantity: number) => void;
}

function AdditionalLuggage({ ancillary, onChange }: AncillaryProps) {
  const { t } = useTranslation();

  return (
    <div className="block-section flex flex-col gap-4 mb-6">
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex gap-4">
          <Icon src="luggage_line" />
          <div className="text-base text-text-strong">
            <div className="font-jakarta text-[20px]">
              {t("options.luggage")}
            </div>
            <p>{t("options.luggageDimensions")}</p>
          </div>
        </div>

        <div className="flex flex-row gap-2 justify-between md:flex-col md:text-right md:items-end">
          <div className="md:order-1">
            <p>
              {t("options.luggageMaximum", {
                price: formatPrice(ancillary.price),
                max: 2,
              })}
            </p>
          </div>
          <Stepper min={0} max={2} initial={0} onChange={onChange} />
        </div>
      </div>
      <BannerMessage type="warning">
        {t("options.luggageWarning")}
      </BannerMessage>
    </div>
  );
}

function FoldedBike({ ancillary, onChange }: AncillaryProps) {
  const { t } = useTranslation();

  return (
    <div className="block-section flex flex-col gap-4 mb-6">
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex gap-4">
          <Icon src="bike_line" />
          <div className="text-base text-text-strong">
            <div className="font-jakarta text-[20px]">
              {t("options.foldedBike")}
            </div>
            <p>{t("options.foldedBikeDimensions")}</p>
          </div>
        </div>

        <div className="flex flex-row gap-2 justify-between md:flex-col md:text-right md:items-end">
          <div className="md:order-1">
            <p>
              {t("options.foldedBikeMaximum", {
                price: formatPrice(ancillary.price),
                max: ancillary.quantity,
              })}
            </p>
          </div>
          <Stepper min={0} max={ancillary.quantity} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}

function DisassembledBike({ ancillary, onChange }: AncillaryProps) {
  const { t } = useTranslation();

  return (
    <div className="block-section flex flex-col gap-4 mb-6">
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex gap-4">
          <Icon src="bike_line" />
          <div className="text-base text-text-strong">
            <div className="font-jakarta text-[20px]">
              {t("options.nonFoldedBike")}
            </div>
            <p>{t("options.nonFoldedBikeWarning")}</p>
          </div>
        </div>

        <div className="flex flex-row gap-2 justify-between md:flex-col md:text-right md:items-end">
          <div className="md:order-1">
            <p>
              {t("options.nonFoldedBikeMaximum", {
                price: formatPrice(ancillary.price),
                max: ancillary.quantity,
              })}
            </p>
          </div>
          <Stepper min={0} max={ancillary.quantity} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}
